/**
 * P1.8 — Monitoring seam.
 *
 * Owns `window.reportError`, the hook `ErrorBoundary`, `window.onerror`,
 * `window.onunhandledrejection`, and `logger.warn/error` all funnel through.
 *
 * No third-party error-tracking processor is wired here. GlitchTip (Burke
 * Software and Consulting LLC, USA) was removed 2026-08-23: it received
 * visitor IP addresses and page URLs from every browser via `@sentry/react`
 * and the CSP `report-uri`, and no Art. 28(3) GDPR processor contract
 * existed for it — none was offered, and GDPR Art. 28(1) prohibits using a
 * processor without one. See `subprocessors.md` (public-lazysem) and
 * `docs/security.md` §Observability.
 *
 * The reporter below is a local, browser-only stub: it logs through
 * `logger.error` in dev and is a deliberate no-op in production, so
 * `ErrorBoundary`, `logger.warn`/`logger.error`, `window.onerror`, and
 * `window.onunhandledrejection` keep a single call target and never throw —
 * but nothing is sent off the visitor's device. Re-introducing a processor
 * here needs a signed DPA, a subprocessors.md entry, and a Privacy Policy
 * update before any code ships.
 */

import { logger } from './logger';

type ReporterContext = Record<string, unknown> | undefined;
type GlobalReporter = (error: unknown, context?: ReporterContext) => void;

// The DOM ships its own `Window.reportError(error)` (1-arg) on recent
// browsers. Our seam takes a 2nd `context` arg, so we cast through `unknown`
// rather than intersecting with `Window` — intersecting would fold the native
// 1-arg signature in and reject our 2-arg assignment.
type WindowWithReporter = { reportError?: GlobalReporter };

let initialized = false;

/**
 * Install the global error reporter. Idempotent — safe to call multiple
 * times; only the first invocation wins.
 *
 * Must be called from `main.tsx` BEFORE React mounts so
 * `window.onunhandledrejection` is trapped for any promise rejection that
 * happens during module evaluation of the app bundle.
 */
export function initMonitoring(): void {
  if (initialized) return;
  initialized = true;

  if (typeof window === 'undefined') return;
  const w = window as unknown as WindowWithReporter;

  if (!w.reportError) {
    w.reportError = defaultReporter;
  }

  window.addEventListener('unhandledrejection', (event) => {
    w.reportError?.(event.reason, { kind: 'unhandledrejection' });
  });
  window.addEventListener('error', (event) => {
    w.reportError?.(event.error ?? event.message, { kind: 'window.error' });
  });
}

function defaultReporter(error: unknown, context?: ReporterContext): void {
  // Kept logger-only in dev so the dev loop is unchanged. In prod this is a
  // deliberate no-op: there is no error-tracking backend configured, so
  // callers never throw and nothing leaves the browser.
  if (import.meta.env.DEV) {
    logger.error('[reportError]', error, context);
  }
}

/** Test-only: undo `initMonitoring()` so suites can start from a clean slate. */
export function __resetMonitoringForTests(): void {
  initialized = false;
  if (typeof window !== 'undefined') {
    // Leave window.reportError as-is; caller controls it.
  }
}
